import { useEffect, useRef, useState } from 'react';
import type { AssistantChatMessage, AssistantToolInvocation } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Icon } from '../../components/ui/Icon.js';
import { sendAssistantChat } from './api.js';

interface AssistantPanelProps {
  open: boolean;
  onClose: () => void;
}

interface DisplayMessage extends AssistantChatMessage {
  tools?: AssistantToolInvocation[];
}

/**
 * Slide-over "Ask PSA" chat panel. Read-only: the assistant answers questions about the
 * data (scoped to the member's permissions) and how the system works. Renders replies as
 * plain text and shows which read-only tools it used for transparency.
 */
export function AssistantPanel({ open, onClose }: AssistantPanelProps): JSX.Element | null {
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  if (!open) return null;

  const send = async (): Promise<void> => {
    const text = input.trim();
    if (!text || busy) return;
    setError(null);
    const nextHistory: DisplayMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(nextHistory);
    setInput('');
    setBusy(true);
    try {
      const wire: AssistantChatMessage[] = nextHistory.map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const response = await sendAssistantChat(wire);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: response.reply, tools: response.toolInvocations },
      ]);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        type="button"
        aria-label="Close assistant"
        className="flex-1 bg-slate-900/30 dark:bg-black/60"
        onClick={onClose}
      />
      <aside className="flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-xl">
        <header className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
          <Icon name="assistant" className="h-5 w-5 text-brand-600" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-900">Ask PSA</p>
            <p className="text-xs text-slate-500">Read-only assistant · answers use your access</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <Icon name="close" className="h-5 w-5" />
          </button>
        </header>

        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {messages.length === 0 ? (
            <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
              <p className="font-medium text-slate-800">Ask about your data or how PSA works.</p>
              <ul className="mt-2 list-disc space-y-1 pl-4 text-xs">
                <li>&ldquo;How many open tickets does Interdev have?&rdquo;</li>
                <li>&ldquo;List active agreements for GoodRX.&rdquo;</li>
                <li>&ldquo;What does ticket #14570 look like?&rdquo;</li>
                <li>&ldquo;How does managed-services overage billing work?&rdquo;</li>
              </ul>
            </div>
          ) : null}

          {messages.map((message, index) => (
            <MessageBubble key={index} message={message} />
          ))}

          {busy ? <p className="text-xs text-slate-500">Thinking…</p> : null}
          {error ? (
            <p className="rounded-lg bg-rose-50 p-2 text-xs text-rose-700">{error}</p>
          ) : null}
        </div>

        <div className="border-t border-slate-200 p-3">
          <textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            placeholder="Ask a question…"
            className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => void send()}
              disabled={busy || !input.trim()}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              Send
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function MessageBubble({ message }: { message: DisplayMessage }): JSX.Element {
  const isUser = message.role === 'user';
  return (
    <div className={isUser ? 'flex justify-end' : 'flex justify-start'}>
      <div
        className={
          isUser
            ? 'max-w-[85%] whitespace-pre-wrap rounded-lg bg-brand-600 px-3 py-2 text-sm text-white'
            : 'max-w-[85%] whitespace-pre-wrap rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-800'
        }
      >
        {message.content}
        {message.tools && message.tools.length > 0 ? (
          <details className="mt-2 text-xs text-slate-500">
            <summary className="cursor-pointer select-none">
              Used {message.tools.length} lookup{message.tools.length === 1 ? '' : 's'}
            </summary>
            <ul className="mt-1 space-y-1">
              {message.tools.map((tool, index) => (
                <li key={index} className={tool.ok ? '' : 'text-rose-600'}>
                  <span className="font-mono">{tool.tool}</span> — {tool.summary}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </div>
  );
}
